import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { ManufacturersService } from '../manufacturers.service'
import { ManufacturersRepository } from '../manufacturers.repository'
import { ManufacturerSearchParams } from '../models/manufacturer-search.model'
import { Manufacturer } from '../models/manufacturer.entity'
import { Page } from '../../../models/util.model'
import { map, of, switchMap } from 'rxjs'
import { assign } from 'lodash'

interface ManufacturersListingState {
  filters?: ManufacturerSearchParams
  data?: Manufacturer[]
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const MANUFACTURERS_LISTING_INITIAL_STATE: ManufacturersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class ManufacturersListingStore {
  constructor(
    private manufacturersService: ManufacturersService,
    private manufacturersRepository: ManufacturersRepository,
  ) {}

  private state = signal<ManufacturersListingState>(
    MANUFACTURERS_LISTING_INITIAL_STATE,
  )

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly manufacturers = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)

  // Effects

  async search(
    params?: ManufacturerSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.manufacturersRepository
        .searchManufacturers$(params ?? {}, returnAll)
        .pipe(
          switchMap((page) => {
            // If no data, just return the page as-is
            if (!page.data || page.data.length === 0) {
              return of(page)
            }

            // Get product counts and merge them into the manufacturers
            return this.manufacturersService
              .countProducts$(page.data.map((m) => m._id))
              .pipe(
                map((counts) => {
                  // Merge counts into manufacturers
                  const dataWithCounts = page.data.map((manufacturer) =>
                    assign(
                      manufacturer,
                      counts.find((count) => count._id === manufacturer._id),
                    ),
                  )
                  return { ...page, data: dataWithCounts }
                }),
              )
          }),
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(data)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async delete(manufacturer: Manufacturer): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.manufacturersRepository
        .alert('MANUFACTURER_DELETION', manufacturer)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.manufacturersService.delete$(manufacturer._id).subscribe({
              next: () => {
                resolve(true)
                this.manufacturersRepository.notify({
                  code: 'MANUFACTURER_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(data: Page<Manufacturer>) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: ManufacturerSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: ManufacturerSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: ManufacturerSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof ManufacturerSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
